import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

import { ProductContextDirective } from 'ish-core/directives/product-context.directive';
import { AppFacade } from 'ish-core/facades/app.facade';
import { ProductContextFacade } from 'ish-core/facades/product-context.facade';
import { ShoppingFacade } from 'ish-core/facades/shopping.facade';
import { SkuQuantityType } from 'ish-core/models/product/product.helper';
import { GenerateLazyComponent } from 'ish-core/utils/module-loader/generate-lazy-component.decorator';

import { CustomQuickorderFacade } from './../../facades/custom-quickorder.facade';

/**
 * The Quick Add Products Component displays a form to insert multiple product sku and quantity to add them to the cart.
 */
@Component({
  selector: 'custom-quickorder-flyout',
  templateUrl: './custom-quickorder-flyout.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@GenerateLazyComponent()
export class CustomQuickorderFlyoutComponent implements OnInit {
  @ViewChildren(ProductContextDirective) contexts: QueryList<{ context: ProductContextFacade }>;
  @ViewChild('validateFlyoutForm', { static: true }) validateFlyoutForm: ElementRef<HTMLElement>;
  quickOrderForm: FormGroup = new FormGroup({});
  model: { addProducts: SkuQuantityType[] } = { addProducts: [] };
  options: FormlyFormOptions = {};
  fields: FormlyFieldConfig[];
  quickOrderItems: SkuQuantityType[];
  isSticky$: Observable<boolean>;

  inValidQuantity: boolean;
  numberOfRows = 7;
  constructor(
    private translate: TranslateService,
    private shoppingFacade: ShoppingFacade,
    private customQuickorderFacade: CustomQuickorderFacade,
    private appFacade: AppFacade
  ) {}

  ngOnInit() {
    this.initModel();
    this.fields = this.getFields();
    this.isSticky$ = this.appFacade.stickyHeader$;
  }

  reset() {
    this.options.resetModel();
    this.initModel();
    document.getElementById('quickorderFlyoutView').classList.add('d-none');
  }

  onAddProducts() {
    const products = this.model.addProducts.filter((p: SkuQuantityType) => !!p.sku && !!p.quantity);
    if (products.length > 0) {
      products.forEach(product => {
        this.shoppingFacade.addProductToBasket(product.sku, product.quantity);
      });
    }
    this.reset();
  }

  onSubmit(type: string) {
    const products = this.model.addProducts.filter((p: SkuQuantityType) => !!p.sku && !!p.quantity);
    if (products.length > 0) {
      if (type === 'AddProducts') {
        const quickOrderItems = products.map(record => ({
          sku: record.sku,
          quantity: +record.quantity,
        }));
        this.customQuickorderFacade.addFlyOutQuickOrderToBasket(quickOrderItems);
      }
      if (type === 'AddMoreParts') {
        const quickOrderItems = products.map(record => ({
          productSKU: record.sku,
          quantity: +record.quantity,
        }));
        this.customQuickorderFacade.createQuickOrderFlyoutItems(quickOrderItems);
      }
    }
    this.reset();
  }

  private initModel() {
    this.model = { addProducts: [] };
    for (let i = 0; i < this.numberOfRows; i++) {
      this.model.addProducts.push({ sku: '', quantity: 1 });
    }
  }

  isInValidQuantity() {
    const isQuickOrderFormEmpty = this.model.addProducts.every(product => product.sku === '');
    const htmlElement: HTMLElement = this.validateFlyoutForm?.nativeElement;
    const validationMessageClass = htmlElement?.querySelectorAll('.qty-validation-message');
    this.inValidQuantity = false;
    if (validationMessageClass?.length || isQuickOrderFormEmpty) this.inValidQuantity = true;
  }

  /**
   * returns the field with a repeating type
   * repeat contains the representing of the form with form fields and links to add and remove lines (check quickorder-repeat-form.component for more information)
   * the field array give the input field with validators of the sku for each line (which are represented by the objects of the model array)
   */
  private getFields(): FormlyFieldConfig[] {
    return [
      {
        key: 'addProducts',
        type: 'flyout',
        props: {
          addText: 'quickorder.page.add.row',
          addMoreText: 'quickorder.page.add.row.multiple',
          numberMoreRows: this.numberOfRows,
        },
        fieldArray: {
          fieldGroupClassName: 'row list-item-row py-2',
          fieldGroup: [
            {
              key: 'sku',
              type: 'quick-order-search-input-field',
              className: 'col-12 list-item search-container',
              props: {
                fieldClass: 'col-12',
                placeholder: 'dias.quick_order.item_placeholder',
                attributes: { autocomplete: 'off' },
              },
              expressionProperties: {
                'templateOptions.required': (control: SkuQuantityType) => {
                  if (control.sku === '') return false;
                  else return !!control.sku && !!control.quantity;
                },
              },
              modelOptions: {
                updateOn: 'change',
                debounce: { default: 1 },
              },
              validation: {
                messages: {
                  existingProduct: (_, field) =>
                    this.translate.get('dias.quickorder.sku.alreadyexists.error', {
                      0: field.parent.fieldGroup[0].formControl.value,
                      1:
                        this.model.addProducts.findIndex(
                          (product, index) =>
                            product.sku === field.parent.fieldGroup[0].formControl.value &&
                            +field.parent.key !== index &&
                            //@ts-ignore
                            this.quickOrderForm.controls.addProducts?.controls[index].valid
                        ) + 1,
                    }),
                  validProduct: (_, field) =>
                    this.translate.get('dias.quickorder.page.error.invalid.product', {
                      0: this.model.addProducts[parseInt(field.parent.key.toString(), 10)].sku,
                    }),
                },
              },
            },
          ],
        },
      },
    ];
  }
}
